import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card'
import { Button, ButtonText } from '@/components/ui/button'

import { Link } from 'react-router-dom'
import RegisterFooter from './RegisterFooter'
import { TextField } from '@/components/ui/TextField'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import RegisterSuccess from './RegisterSuccess'
import useRegisterAccount from '@/services/api/auth/useRegisterAccount.js'

export default function RegisterWithEmailPage() {
  const schema = z
    .object({
      name: z.string().min(1, 'The name field is required.'),
      lastName: z.string().min(1, 'The last name field is required.'),
      email: z.string().email().min(1, 'The email is required.'),
      password: z.string().min(1, 'The password is required.'),
      passwordConfirm: z.string(),
    })
    .refine((data) => data.password === data.passwordConfirm, {
      message: "Passwords don't match",
      path: ['passwordConfirm'], // path of error
    })

  const form = useForm({
    resolver: zodResolver(schema),
  })

  const { setError, handleSubmit, formState, register, getValues } = form

  const { registerAccount, registerAccountStatus } =
    useRegisterAccount(setError)

  const { errors } = formState

  function onSubmit(values) {
    const { name, lastName, email, password } = values

    registerAccount({
      name,
      lastName,
      email,
      password,
    })
  }
  const email = getValues('email')

  if (registerAccountStatus.status === 'success')
    return (
      <div className="pf-auth grid min-h-screen">
        <div className="place-self-center">
          <RegisterSuccess
            email={email}
            goBack={() => registerAccountStatus.reset()}
          />
        </div>
      </div>
    )

  return (
    <div className="pf-auth grid min-h-screen">
      <Card className="place-self-center">
        <CardHeader>
          <CardTitle>Create your account</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-3">
              <div>
                <TextField
                  statusText={errors?.name?.message}
                  status={errors?.name ? 'error' : ''}
                  large
                >
                  <TextField.Label>Name</TextField.Label>
                  <TextField.Input {...register('name')} />
                </TextField>
              </div>
              <div>
                <TextField
                  statusText={errors?.lastName?.message}
                  status={errors?.lastName ? 'error' : ''}
                  large
                >
                  <TextField.Label>Last Name</TextField.Label>
                  <TextField.Input {...register('lastName')} />
                </TextField>
              </div>
              <div>
                <TextField
                  statusText={errors?.email?.message}
                  status={errors?.email ? 'error' : ''}
                  large
                >
                  <TextField.Label>Email</TextField.Label>
                  <TextField.Input {...register('email')} />
                </TextField>
              </div>

              <div>
                <TextField
                  statusText={errors?.password?.message}
                  status={errors?.password ? 'error' : ''}
                >
                  <TextField.Label>Create Password</TextField.Label>
                  <TextField.PasswordInput
                    {...register('password')}
                    autoComplete="new-password"
                  />
                </TextField>
              </div>

              <div>
                <TextField
                  statusText={errors?.passwordConfirm?.message}
                  status={errors?.passwordConfirm ? 'error' : ''}
                >
                  <TextField.Label>Confirm Password</TextField.Label>
                  <TextField.PasswordInput {...register('passwordConfirm')} />
                </TextField>
              </div>
            </div>
            <div className="mt-7 grid gap-2">
              <Link to="/register">
                <Button
                  type="button"
                  size="lg"
                  className="w-full"
                  variant="secondary"
                >
                  <ButtonText>Back</ButtonText>
                </Button>
              </Link>
              <Button
                loading={registerAccountStatus.status === 'pending'}
                type="submit"
                size="lg"
                className="w-full"
              >
                <ButtonText>Create Account</ButtonText>
              </Button>
            </div>
          </form>
        </CardContent>
        <CardFooter>
          <RegisterFooter />
        </CardFooter>
      </Card>
    </div>
  )
}
